// Finishing layer: scroll reveals, stat count-up, header scroll state and the
// mobile sticky trial button. No dependencies. Reveals only run when the head
// script has added `js-motion` (JS on + reduced motion off); the header shadow
// and sticky button work for everyone.
const root = document.documentElement;
const motion = root.classList.contains('js-motion');
const hasIO = 'IntersectionObserver' in window;

// Header: shrink + shadow past 40px. A 1px sentinel avoids scroll listeners.
const header = document.querySelector('.site-header');
if (header && hasIO) {
  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;top:40px;left:0;width:1px;height:1px;pointer-events:none';
  document.body.prepend(sentinel);
  new IntersectionObserver(([entry]) => {
    header.classList.toggle('is-scrolled', !entry.isIntersecting);
  }).observe(sentinel);
}

// Scroll reveals.
const REVEAL = [
  'main h1', 'main h2', 'main p', 'main .richtext', 'main dl', 'main table', 'main details',
  'main ul[role="list"] > li', 'main ol[role="list"] > li', 'main .rounded-card', 'main .overflow-hidden:has(img)'
].join(',');

if (motion && hasIO) {
  const candidates = [...document.querySelectorAll(REVEAL)].filter(
    (el) => !el.closest('[data-no-reveal], header, footer, dialog, .sr-only') && !el.classList.contains('sr-only')
  );
  candidates.forEach((el) => el.setAttribute('data-rv', ''));
  // Only the outermost candidate animates, so nested items don't stack effects.
  const targets = candidates.filter((el) => {
    const outer = el.parentElement && el.parentElement.closest('[data-rv]');
    if (outer) el.removeAttribute('data-rv');
    return !outer;
  });

  const io = new IntersectionObserver((entries) => {
    const entering = entries.filter((e) => e.isIntersecting).map((e) => e.target);
    // Items entering together stagger by 90ms, grouped per parent row.
    const perParent = new Map();
    entering.forEach((el) => {
      const i = perParent.get(el.parentElement) || 0;
      perParent.set(el.parentElement, i + 1);
      el.style.setProperty('--rv-d', `${Math.min(i, 5) * 90}ms`);
      el.classList.add('is-in');
      io.unobserve(el);
      el.addEventListener('transitionend', () => el.style.removeProperty('--rv-d'), { once: true });
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
  targets.forEach((el) => io.observe(el));
}

// Stat count-up (2017 from 2000, 1850 from 1000, others from 0; suffixes kept).
if (motion && hasIO) {
  const stats = document.querySelectorAll('[data-count]');
  const ease = (t) => 1 - Math.pow(1 - t, 3);
  const statIO = new IntersectionObserver((entries) => {
    entries.forEach(({ isIntersecting, target }) => {
      if (!isIntersecting) return;
      statIO.unobserve(target);
      const match = target.textContent.trim().match(/^(\d+)(.*)$/);
      if (!match) return;
      const end = Number(match[1]);
      const suffix = match[2];
      const start = Number(target.dataset.countFrom || 0);
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min((now - t0) / 1400, 1);
        target.textContent = `${Math.round(start + (end - start) * ease(p))}${suffix}`;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }, { threshold: 0.5 });
  stats.forEach((el) => statIO.observe(el));
}

// Mobile sticky trial button: visible once the hero (or first screen) is gone,
// hidden while the final call to action, a trial strip or the footer is on screen.
const sticky = document.querySelector('.sticky-trial');
if (sticky && hasIO) {
  let pastHero = false;
  const blockers = new Set();
  const update = () => sticky.classList.toggle('is-visible', pastHero && blockers.size === 0);
  const hero = document.querySelector('[data-hero]');
  if (hero) {
    new IntersectionObserver(([e]) => { pastHero = !e.isIntersecting; update(); }).observe(hero);
  } else {
    const marker = document.createElement('div');
    marker.setAttribute('aria-hidden', 'true');
    marker.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:100svh;pointer-events:none';
    document.body.prepend(marker);
    new IntersectionObserver(([e]) => { pastHero = !e.isIntersecting; update(); }).observe(marker);
  }
  const blockIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => (e.isIntersecting ? blockers.add(e.target) : blockers.delete(e.target)));
    update();
  });
  document.querySelectorAll('[data-sticky-hide], body > footer, footer').forEach((el) => blockIO.observe(el));
}
